import { BeakerIcon } from '@heroicons/react/24/solid';
import { Chip, Input, Spinner } from '@heroui/react';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DEV_LOGIN_OTP_BYPASS_CODE,
  DEV_SEED_USERS,
  type DevSeedUser,
  type DevSeedUserRole,
} from '@/app/helpers/dev-seed-users.ts';
import { useAuth } from '@/app/hooks/auth-provider.tsx';
import { Avatar } from '@/primitives/avatar.tsx';
import { Dialog, DialogBody, DialogTitle } from '@/primitives/dialog.tsx';
import { Text } from '@/primitives/text.tsx';

const getRoleChipColor = (role: DevSeedUserRole) => {
  if (role === 'Admin') {
    return 'warning';
  }

  if (role === 'Rep') {
    return 'success';
  }

  return 'primary';
};

const getRoleInitials = (seedUser: DevSeedUser) => {
  if (seedUser.role === 'Admin') {
    return 'AD';
  }

  const number = seedUser.label.replace(/\D/g, '');
  return `${seedUser.role[0]}${number}`;
};

const DevSeedUserRow = ({
  seedUser,
  isCurrentUser,
  isBusy,
  isDisabled,
  onSelect,
}: {
  seedUser: DevSeedUser;
  isCurrentUser: boolean;
  isBusy: boolean;
  isDisabled: boolean;
  onSelect: (emailAddress: string) => void;
}) => {
  const rowClassName = isCurrentUser
    ? 'border-primary-200 bg-primary-50 hover:bg-primary-100'
    : 'border-transparent bg-default-50 hover:bg-default-100';

  return (
    <button
      type='button'
      disabled={isDisabled}
      onClick={() => onSelect(seedUser.emailAddress)}
      className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors disabled:opacity-60 ${rowClassName}`}
    >
      <Avatar
        initials={getRoleInitials(seedUser)}
        className='size-9 shrink-0 bg-zinc-900 text-xs text-white'
      />
      <span className='min-w-0 flex-1'>
        <span className='flex items-center gap-2'>
          <span className='truncate font-semibold text-zinc-900'>{seedUser.label}</span>
          <Chip
            size='sm'
            variant='flat'
            color={getRoleChipColor(seedUser.role)}
            classNames={{
              base: 'h-5 px-1.5',
              content: 'text-[10px] px-1 font-medium',
            }}
          >
            {seedUser.role}
          </Chip>
        </span>
        <span className='mt-0.5 block truncate text-sm text-default-500'>
          {seedUser.emailAddress}
        </span>
      </span>
      {isBusy && <Spinner size='sm' color='current' />}
      {isCurrentUser && !isBusy && (
        <span className='shrink-0 text-[11px] font-medium text-primary-600'>Signed in</span>
      )}
    </button>
  );
};

const DevFastLogin = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loggingInEmail, setLoggingInEmail] = useState('');
  const [error, setError] = useState('');
  const { logInWithOtp, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return DEV_SEED_USERS;
    }

    return DEV_SEED_USERS.filter(seedUser => {
      const haystack = `${seedUser.label} ${seedUser.role} ${seedUser.emailAddress}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [searchQuery]);

  const onClose = () => {
    setIsOpen(false);
    setSearchQuery('');
    setError('');
  };

  const onSelectUser = async (emailAddress: string) => {
    setError('');
    setLoggingInEmail(emailAddress);

    try {
      await logInWithOtp({
        emailAddress,
        otpCode: DEV_LOGIN_OTP_BYPASS_CODE,
      });
      onClose();
      const redirectTo = location.state?.redirectTo || '/';
      navigate(redirectTo, { replace: true });
    } catch {
      setError(
        'Could not sign in as that seed user. Reset the local database if these emails are not in your seed data.',
      );
    } finally {
      setLoggingInEmail('');
    }
  };

  if (!import.meta.env.DEV) {
    return null;
  }

  return (
    <>
      <button
        type='button'
        className='fixed top-2 left-2 z-[200] flex size-7 items-center justify-center rounded-full bg-amber-400 text-zinc-900 shadow-md hover:bg-amber-300'
        title='Dev fast login'
        onClick={() => setIsOpen(true)}
      >
        <BeakerIcon className='size-4' />
      </button>
      <Dialog open={isOpen} onClose={onClose} size='md'>
        <DialogTitle>Dev fast login</DialogTitle>
        <Text className='mt-2'>
          Signs in with the local OTP bypass code. Only seed users from a local database reset.
        </Text>
        {user && (
          <Text className='mt-1'>
            Currently signed in as {user.firstName} {user.lastName} ({user.emailAddress}).
          </Text>
        )}
        <DialogBody>
          <Input
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder='Search admin, staff, or rep...'
            isClearable
            onClear={() => setSearchQuery('')}
            className='mb-3'
          />
          {error && <Text className='mb-3 text-red-600'>{error}</Text>}
          <div className='flex max-h-80 flex-col gap-1.5 overflow-y-auto pr-1'>
            {filteredUsers.length === 0 && <Text>No seed users match that search.</Text>}
            {filteredUsers.map(seedUser => (
              <DevSeedUserRow
                key={seedUser.emailAddress}
                seedUser={seedUser}
                isCurrentUser={user?.emailAddress === seedUser.emailAddress}
                isBusy={loggingInEmail === seedUser.emailAddress}
                isDisabled={!!loggingInEmail}
                onSelect={onSelectUser}
              />
            ))}
          </div>
        </DialogBody>
      </Dialog>
    </>
  );
};

export default DevFastLogin;
